import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';

interface AdminUser {
  user_id: string;
  nickname: string;
  level: number;
  score: number;
  admin_role: string | null;
  sanction_type: string | null;
  sanction_until: string | null;
  warning_count: number;
  post_count: number;
  comment_count: number;
  is_premium: boolean;
  is_deleted: boolean;
  created_at: string;
  last_active_at: string | null;
}

interface ListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

const LIMIT = 20;

function statusBadge(u: AdminUser) {
  if (u.is_deleted) return <Badge variant="fall">탈퇴</Badge>;
  if (u.sanction_type === 'ban') return <Badge variant="fall">영구정지</Badge>;
  if (u.sanction_until && new Date(u.sanction_until) > new Date()) {
    return <Badge variant="fall">정지중</Badge>;
  }
  return <Badge variant="rise">정상</Badge>;
}

/** 5분 이내 활동(=온라인) 판정 */
function isOnline(lastActiveAt: string | null): boolean {
  if (!lastActiveAt) return false;
  return Date.now() - new Date(lastActiveAt).getTime() < 5 * 60 * 1000;
}

/** 온라인 표시 초록 점 — last_active_at < 5분이면 표시 */
function OnlineDot({ lastActiveAt }: { lastActiveAt: string | null }) {
  if (!isOnline(lastActiveAt)) return null;
  return (
    <span
      title="온라인 (5분 이내 활동)"
      aria-label="온라인"
      className="inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.18)]"
    />
  );
}

function roleBadge(role: string | null) {
  if (!role) return null;
  const label = role === 'readonly' ? 'viewer' : role;
  const variant = role === 'super' ? 'premium' : role === 'content' ? 'primary' : 'outline';
  return <Badge variant={variant as 'primary' | 'premium' | 'outline'}>{label}</Badge>;
}

function fmtDate(iso: string | null) {
  if (!iso) return '-';
  return new Date(iso).toISOString().slice(0, 10);
}

export function UsersListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const role = searchParams.get('role') ?? '';
  const suspended = searchParams.get('suspended') === 'true';
  const page = Number(searchParams.get('page') ?? 1);

  const [draftQ, setDraftQ] = useState(q);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', { q, role, suspended, page }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (role) params.set('role', role);
      if (suspended) params.set('suspended', 'true');
      params.set('page', String(page));
      params.set('limit', String(LIMIT));
      return get<ListResponse>(`/admin/users?${params.toString()}`);
    },
  });

  /* 현재 온라인(5분 이내) 카운트 — /admin/stats/overview 의 onlineNow */
  const { data: overview } = useQuery({
    queryKey: ['admin', 'stats-overview'],
    queryFn: () => get<{ onlineNow: number }>('/admin/stats/overview'),
    refetchInterval: 30_000, // 30초 간격 갱신
    staleTime: 30_000,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  function applySearch() {
    const next = new URLSearchParams(searchParams);
    if (draftQ) next.set('q', draftQ);
    else next.delete('q');
    next.set('page', '1');
    setSearchParams(next);
  }

  function setRole(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('role', value);
    else next.delete('role');
    next.set('page', '1');
    setSearchParams(next);
  }

  function toggleSuspended() {
    const next = new URLSearchParams(searchParams);
    if (suspended) next.delete('suspended');
    else next.set('suspended', 'true');
    next.set('page', '1');
    setSearchParams(next);
  }

  function goPage(p: number) {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  }

  return (
    <div className="w-full">
      <h1 className="mb-6 text-2xl font-extrabold text-[var(--color-text)]">유저 관리</h1>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="닉네임 검색"
          value={draftQ}
          onChange={(e) => setDraftQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          className="max-w-xs"
        />
        <Button onClick={applySearch} size="sm">검색</Button>

        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-sm"
        >
          <option value="">권한 전체</option>
          <option value="super">super</option>
          <option value="content">content</option>
          <option value="readonly">viewer</option>
        </select>

        <Button
          variant={suspended ? 'danger' : 'ghost'}
          size="sm"
          onClick={toggleSuspended}
        >
          {suspended ? '정지유저만 ✓' : '정지유저만'}
        </Button>

        <div className="ml-auto flex items-center gap-3 text-xs text-[var(--color-text-sub)]">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            <span>
              온라인{' '}
              <span className="font-bold text-emerald-700 tabular-nums">{overview?.onlineNow ?? 0}</span>
              <span className="ml-0.5">명</span>
            </span>
          </span>
          <span>총 {data?.total ?? 0}명</span>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg)] text-left">
              <tr>
                <th className="p-3">닉네임</th>
                <th className="p-3">권한</th>
                <th className="p-3">레벨</th>
                <th className="p-3">글/댓글</th>
                <th className="p-3">경고</th>
                <th className="p-3">상태</th>
                <th className="p-3">가입일</th>
                <th className="p-3">최근접속</th>
              </tr>
            </thead>
            <tbody>
              {data?.users.map((u) => (
                <tr
                  key={u.user_id}
                  className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg)]"
                >
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1.5">
                      <OnlineDot lastActiveAt={u.last_active_at} />
                      <Link
                        to={`/admin/users/${u.user_id}`}
                        className="font-medium text-[var(--color-primary)] hover:underline"
                      >
                        {u.nickname}
                      </Link>
                    </span>
                  </td>
                  <td className="p-3">{roleBadge(u.admin_role)}</td>
                  <td className="p-3">{u.level}</td>
                  <td className="p-3">
                    {u.post_count}/{u.comment_count}
                  </td>
                  <td className="p-3">{u.warning_count}</td>
                  <td className="p-3">{statusBadge(u)}</td>
                  <td className="p-3 text-[var(--color-text-sub)]">{fmtDate(u.created_at)}</td>
                  <td className="p-3 text-[var(--color-text-sub)]">{fmtDate(u.last_active_at)}</td>
                </tr>
              ))}
              {data?.users.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-[var(--color-text-sub)]">
                    조건에 맞는 유저가 없어요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => goPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm text-[var(--color-text-sub)]">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => goPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
}
